import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Popup4868 from '@/components/ui/4868Popup';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form4868MailAddress = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 4868 for Tax Extension: Mailing Address & Deadline"
          description="Learn where to mail IRS Form 4868 for tax extension and the benefits of e-filing. Get faster processing and updates from the IRS."
          keywords="form 4868 instructions, irs form 4868 instructions, personal tax extension, instructions for form 4868, IRS tax extension form 4868, form 4868 filing instructions, Form 1040 extension"
          canonical="resources/tax-extension/form-4868-mailing-addresses-and-filing-guide/"
          ogType="article"
          jsonLdPricing="11.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Popup4868 />
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 4868</span>
          </span>{' '}
          Mailing Address
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                  minHeight: '100%',
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-4868-mailing-address/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 4868 mailing address at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <a
                          href="https://www.taxzerone.com/resources/tax-extension/form-4868-instructions/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 4868
                        </a>{' '}
                        is used by individual taxpayers to request an automatic{' '}
                        <span style={{ fontWeight: 600 }}>
                          6-month extension
                        </span>{' '}
                        to file eligible federal income tax returns
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>Paper filers</span>{' '}
                        must mail Form 4868 to the correct IRS address based on
                        their state, location, return type, and whether they are
                        making a payment.
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>
                          Taxpayers making a payment
                        </span>{' '}
                        generally use a different IRS mailing address than those{' '}
                        <span style={{ fontWeight: 600 }}>
                          not making a payment
                        </span>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>
                          U.S. taxpayers living abroad, Puerto Rico residents,
                          and certain special-status taxpayers
                        </span>{' '}
                        may have different Form 4868 mailing addresses.
                      </span>
                    </li>
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>
                          Form 1040-NR and Form 1040-SS filers
                        </span>{' '}
                        may also need to use specific mailing addresses
                        depending on their circumstances.
                      </span>
                    </li>
                    <li>
                      <span>
                        The deadline for Form 4868 is{' '}
                        <span style={{ fontWeight: 600 }}>April 15</span>.{' '}
                        <a
                          href="https://www.taxzerone.com/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          E-filing Form 4868
                        </a>{' '}
                        eliminates the need to mail the paper form and can
                        provide faster processing and electronic confirmation.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Individual taxpayers use IRS Form 4868 to request an
                      extension for an extra six months to file different types
                      of income tax returns. People who{' '}
                      <Link
                        href="/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        file Form 4868
                      </Link>{' '}
                      get more time to send their tax returns, but not to pay
                      any due taxes.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      This page has important information about mailing
                      addresses for form 4868 that need to be sent by mail.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 2, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 2,
                  }}
                >
                  What is Tax Form 4868?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Individual taxpayers use{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 4868
                  </Link>{' '}
                  to request an extension to file income tax returns, such as
                  Forms 1040, 1040-SR, 1040-NR, or 1040-SS. By completing{' '}
                  <Link
                    href="/resources/tax-extension/form-4868-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 4868
                  </Link>
                  , taxpayers can request a six-month delay.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                  }}
                  className="gradient-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '20px !important',
                      }}
                    >
                      The IRS recommends e-filing for faster and more secure
                      processing.
                    </Typography>
                    <Typography variant="body2">
                      E-file Form 4868 with TaxZerone in just minutes and
                      receive instant confirmation—fast and reliable.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        backgroundColor: '#0bf473',
                        color: '#333',
                        fontSize: 18,
                        fontWeight: 600,
                        '&:hover, &:focus, &:active': {
                          backgroundColor: '#fff',
                        },
                      }}
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868mailaddress`}
                    >
                      E-file Form 4868 Now
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      backgroundColor: '#fff3b1',
                      py: 1,
                      px: 2,
                      border: '2px dashed #000',
                      color: '#000',
                      lineHeight: 1.3,
                    }}
                  >
                    <b>Grab 10% off</b> your filing fee — just enter{' '}
                    <b style={{ fontSize: 18, color: '#c02121' }}>TZOEXT10</b>{' '}
                    at checkout.
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the deadline for filing Form 4868?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The{' '}
                  <Link
                    href="/resources/tax-extension/form-4868-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    due date to file Form 4868
                  </Link>{' '}
                  for the 2025 tax year is{' '}
                  <span style={{ fontWeight: 600 }}> April 15, 2026.</span>{' '}
                  Taxpayers who file for the fiscal year must file by the
                  original due date of their return.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  For taxpayers outside the U.S.:
                </Typography>
                <Box component="ul" sx={{ '& li': { mb: 1.5 }, mb: 3 }}>
                  <li>
                    If you are a U.S. citizen or resident living or working
                    outside of the U.S. and Puerto Rico, you automatically get
                    an extra two months to file and pay your taxes, but interest
                    will still be charged. You have until June 15, 2026 , to
                    file and pay. If you need an extra 4 months, check the box
                    on line 8.
                  </li>
                  <li>
                    If you meet the &quot;bona fide residence or physical
                    presence&quot; test, you may be able to get special tax
                    treatment. Fill out Form 2350 to ask for more time to file
                    your return if you don&apos;t think you&apos;ll be able to
                    by the due date.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Form 1040-NR filers:
                </Typography>
                <Box component="ul" sx={{ '& li': { mb: 1.5 }, mb: 5 }}>
                  <li>
                    File Form 4868 by the return due date. If you didn&apos;t
                    get wages related to U.S. tax withholding and your return is
                    due June 15, 2026, check the box on line 9.
                  </li>
                  <li>
                    The longest that you can get an extension is six months,
                    until October 15, 2026, unless there are special
                    circumstances. Find out more in Pub. 54.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 4868 Mailing Address
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The mailing address for submitting the paper copy to file a
                  tax extension are as follows:
                </Typography>

                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 700,
                              verticalAlign: 'top',
                            }}
                          >
                            If you live in
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 700,
                              verticalAlign: 'top',
                            }}
                          >
                            If you&apos;re making a payment, send Form 4868 to
                            the Internal Revenue Service:
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 700,
                              verticalAlign: 'top',
                            }}
                          >
                            If you’re not making a payment, send Form 4868 to
                            the Department of the Treasury, Internal Revenue
                            Service Center:
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Florida, Georgia, Louisiana, Mississippi,
                            North Carolina, South Carolina, Tennessee, Texas
                          </TableCell>
                          <TableCell>
                            P.O. Box 1302, Charlotte, NC 28201-1302
                          </TableCell>
                          <TableCell>Austin, TX 73301-0045</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Arizona, Arkansas, New Mexico, Oklahoma
                          </TableCell>
                          <TableCell>
                            P.O. Box 931300, Louisville, KY 40293-1300
                          </TableCell>
                          <TableCell>Austin, TX 73301-0045</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            Connecticut, Delaware, District of Columbia,
                            Illinois, Indiana, Iowa, Kentucky, Maine, Maryland,
                            Massachusetts, Minnesota, Missouri, New Hampshire,
                            New Jersey, New York, Pennsylvania, Rhode Island,
                            Vermont, Virginia, West Virginia, Wisconsin
                          </TableCell>
                          <TableCell>
                            P.O. Box 931300, Louisville, KY 40293-1300
                          </TableCell>
                          <TableCell>Kansas City, MO 64999-0045</TableCell>
                        </TableRow>
                        {/* <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                               <TableCell>Pennsylvania</TableCell>
                                               <TableCell>
                                                 P.O. Box 802503, Cincinnati, OH 45280-2503
                                               </TableCell>
                                               <TableCell>Kansas City, MO 64999-0045</TableCell>
                                               <TableCell>
                                                 Quickly and Securely file Form 4868 with TaxZerone
                                               </TableCell>
                                             </TableRow> */}
                        <TableRow sx={{}}>
                          <TableCell>
                            Alaska, California, Colorado, Hawaii, Idaho, Kansas,
                            Michigan, Montana, Nebraska, Nevada, North Dakota,
                            Ohio, Oregon, South Dakota, Utah, Washington,
                            Wyoming
                          </TableCell>
                          <TableCell>
                            P.O. Box 931300, Louisville, KY 40293-1300
                          </TableCell>
                          <TableCell>Ogden, UT 84201-0045</TableCell>
                        </TableRow>
                        {/* <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                               <TableCell>
                                                 Alabama, Georgia, North Carolina, South Carolina,
                                                 Tennessee
                                               </TableCell>
                                               <TableCell>
                                                 P.O. Box 1302, Charlotte, NC 28201-1302
                                               </TableCell>
                                               <TableCell>Kansas City, MO 64999-0045</TableCell>
                                               <TableCell>
                                                 Use TaxZerone to simplify your 4868 e-filing process
                                               </TableCell>
                                             </TableRow> */}
                        <TableRow sx={{}}>
                          <TableCell>
                            A foreign country, American Samoa, or Puerto Rico,
                            or are excluding income under Internal Revenue Code
                            section 933, or use an APO or FPO address, or file
                            Form 2555 or 4563, or are a dual-status alien, or
                            are a nonpermanent resident of Guam or the U.S.
                            Virgin Islands
                          </TableCell>
                          <TableCell>
                            P.O. Box 1303, Charlotte, NC 28201-1303
                          </TableCell>
                          <TableCell> Austin, TX 73301-0215 USA </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            All foreign estate and trust Form 1040-NR filers
                          </TableCell>
                          <TableCell>
                            P.O. Box 1303, Charlotte, NC 28201-1303 USA
                          </TableCell>
                          <TableCell>Kansas City, MO 64999-0045 USA</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            All other Form 1040-NR and 1040-SS filers
                          </TableCell>

                          <TableCell>
                            P.O. Box 1303, Charlotte, NC 28201-1303 USA
                          </TableCell>
                          <TableCell>Austin, TX 73301-0215 USA </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> The IRS encourages electronic filing of Form
                  4868 for fast processing.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-file Form 4868 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  As an IRS-authorized e-file provider, TaxZerone offers the
                  simplest process to{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 4868
                  </Link>{' '}
                  in 3 simple steps.
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon
                      sx={{
                        minWidth: 55,
                        mr: 0.5,
                        mt: 0.6,
                        fontWeight: 600,
                        color: '#333',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Step 1:
                    </ListItemIcon>

                    <ListItemText
                      sx={{
                        fontWeight: 400,
                        mb: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Select the tax year and enter personal information.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon
                      sx={{
                        minWidth: 55,
                        mr: 0.5,
                        mt: 0.6,
                        fontWeight: 600,
                        color: '#333',
                      }}
                    >
                      Step 2:
                    </ListItemIcon>

                    <ListItemText
                      sx={{
                        fontWeight: 400,
                        mb: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Enter the estimated tax and payments. TaxZerone will
                      automatically calculate the balance due. You may choose to
                      pay the balance due along with your Form 4868.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon
                      sx={{
                        minWidth: 55,
                        mr: 0.5,
                        mt: 0.6,
                        fontWeight: 600,
                        color: '#333',
                      }}
                    >
                      Step 3:
                    </ListItemIcon>

                    <ListItemText
                      sx={{
                        fontWeight: 400,
                        mb: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Transmit your return and receive an accepted notification
                      from the IRS.
                    </ListItemText>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Have you prepared your personal information, tax liability,
              <br />
              and payments for 2025?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              You can e-file Form 4868 with the IRS within the next 5 minutes.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868mailaddress`}
              >
                Get an Extension Today
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form4868MailAddress;
